import React, { useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Form from '../../Form';
import Modal from '../../Modal';
import {
    InvoiceFormSection,
    centsToDollars,
    dollarsToCents,
    formatCents,
} from './InvoiceFormParts';

const PAYMENT_METHODS = [
    { value: 'manual', label: 'Manual / Off-site' },
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'venmo', label: 'Venmo' },
    { value: 'zelle', label: 'Zelle' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'authorizenet', label: 'Authorize.net' },
    { value: 'ccbill', label: 'CCBill' },
    { value: 'other', label: 'Other' },
];

function ApplyPaymentModal({ invoice, onClose, onSuccess }) {
    const { fetch: authFetch, API_URL } = useAuth();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [values, setValues] = useState(() => ({
        transactionType: 'payment',
        amountDollars: centsToDollars(Math.max(0, invoice.amountDueCents)),
        method: 'manual',
        note: '',
        processorPaymentId: '',
        refundReason: '',
    }));

    const isRefund = values.transactionType === 'refund';

    const handleFormChange = (nextValues, name, value) => {
        if (name === 'transactionType') {
            setValues({
                ...nextValues,
                amountDollars: value === 'refund'
                    ? centsToDollars(Math.max(0, invoice.amountPaidCents))
                    : centsToDollars(Math.max(0, invoice.amountDueCents)),
            });
            return;
        }

        setValues(nextValues);
    };

    const handleSubmit = async (submittedValues) => {
        setError(null);
        setSaving(true);

        const amountCents = dollarsToCents(submittedValues.amountDollars);
        if (!amountCents || amountCents <= 0) {
            setError('Amount must be greater than zero');
            setSaving(false);
            return;
        }

        try {
            const isRefundSubmission = submittedValues.transactionType === 'refund';
            const payload = {
                amountCents,
                method: submittedValues.method,
                note: submittedValues.note.trim(),
                isRefund: isRefundSubmission,
                refundReason: isRefundSubmission ? submittedValues.refundReason.trim() : null,
                processorPaymentId: submittedValues.processorPaymentId.trim() || null,
            };

            const response = await authFetch(
                `${API_URL}/v1/seller/invoices/${invoice.uuid}/pay`,
                { method: 'POST', body: JSON.stringify(payload) }
            );

            const json = await response.json();
            if (!response.ok) {
                setError(json.error || 'Failed to record payment');
                return;
            }

            onSuccess(json.data);
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            onClose={onClose}
            title={isRefund ? 'Record Refund' : 'Record Payment'}
            isOpen={true}
            size="large"
            className="invoice-modal"
        >
            <Form
                values={values}
                onChange={handleFormChange}
                onSubmit={handleSubmit}
                className="invoice-form invoice-payment-form"
                fields={[
                    {
                        name: 'balanceSummary',
                        type: 'render',
                        render: () => (
                            <div className="invoice-payment-summary-grid">
                                <div className="invoices-summary-card">
                                    <span>Total</span>
                                    <strong>{formatCents(invoice.totalCents, invoice.currency)}</strong>
                                </div>
                                <div className="invoices-summary-card">
                                    <span>Paid</span>
                                    <strong>{formatCents(invoice.amountPaidCents, invoice.currency)}</strong>
                                </div>
                                <div className="invoices-summary-card invoices-summary-card--attention">
                                    <span>Due</span>
                                    <strong>{formatCents(invoice.amountDueCents, invoice.currency)}</strong>
                                </div>
                            </div>
                        ),
                    },
                    // {
                    //     name: 'transactionType',
                    //     type: 'render',
                    //     render: ({ value, onChange }) => (
                    //         <InvoiceFormSection title="Transaction type">
                    //             <div className="invoice-choice-grid" role="radiogroup" aria-label="Transaction type">
                    //                 <button
                    //                     type="button"
                    //                     className={`invoice-choice ${value === 'payment' ? 'active' : ''}`}
                    //                     onClick={() => onChange('transactionType', 'payment')}
                    //                     aria-pressed={value === 'payment'}
                    //                 >
                    //                     <strong>Payment received</strong>
                    //                     <span>Apply money toward the invoice balance.</span>
                    //                 </button>
                    //                 <button
                    //                     type="button"
                    //                     className={`invoice-choice ${value === 'refund' ? 'active' : ''}`}
                    //                     onClick={() => onChange('transactionType', 'refund')}
                    //                     aria-pressed={value === 'refund'}
                    //                 >
                    //                     <strong>Refund / adjustment</strong>
                    //                     <span>Record money returned or a balance adjustment.</span>
                    //                 </button>
                    //             </div>
                    //         </InvoiceFormSection>
                    //     ),
                    // },
                    {
                        name: 'paymentDetails',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection
                                title={isRefund ? 'Refund details' : 'Payment details'}
                                description={isRefund ? 'Refunds reduce the amount paid on this invoice.' : 'Record an off-platform or manually verified payment.'}
                            >
                                <div className="invoice-form-grid invoice-form-grid--two">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'amountDollars',
                                                type: 'number',
                                                label: `Amount (${invoice.currency})`,
                                                min: '0.01',
                                                step: '0.01',
                                                required: true,
                                                help: isRefund
                                                    ? `Paid to date: ${formatCents(invoice.amountPaidCents, invoice.currency)}`
                                                    : `Outstanding: ${formatCents(Math.max(0, invoice.amountDueCents), invoice.currency)}`,
                                            },
                                            {
                                                name: 'method',
                                                type: 'select',
                                                label: 'Method',
                                                options: PAYMENT_METHODS,
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                    {
                        name: 'reference',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection title="Reference" description="Keep enough detail to reconcile this transaction later.">
                                <Form
                                    as="div"
                                    values={formValues}
                                    onChange={(nextValues, name, value) => onChange(name, value)}
                                    className="invoice-subform invoice-subform--stacked"
                                    fields={[
                                        {
                                            name: 'note',
                                            label: 'Note',
                                            placeholder: 'e.g. Check #1234, received 2026-05-27',
                                            maxLength: 500,
                                        },
                                        {
                                            name: 'processorPaymentId',
                                            label: 'Processor payment ID',
                                            placeholder: 'e.g. PayPal capture ID or Authorize.net transaction ID',
                                            maxLength: 255,
                                            help: 'Optional, but useful for idempotency and support lookups.',
                                        },
                                        isRefund && {
                                            name: 'refundReason',
                                            label: 'Refund reason',
                                            placeholder: 'Reason for refund or adjustment',
                                            maxLength: 500,
                                        },
                                    ].filter(Boolean)}
                                />
                            </InvoiceFormSection>
                        ),
                    },
                ]}
            >
                {error && <div className="error invoice-form-alert">{error}</div>}

                <div className="form-actions invoice-form-actions">
                    <button type="button" className="button secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="button primary" disabled={saving}>
                        {saving ? 'Saving...' : (isRefund ? 'Record refund' : 'Record payment')}
                    </button>
                </div>
            </Form>
        </Modal>
    );
}

export default ApplyPaymentModal;
